import React, { useState } from 'react';
import { ICellRendererParams } from 'ag-grid-community';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';

export const DEFAULT_NOTE_TRUNCATE_LENGTH = 100;
const TOOLTIP_MAX_LENGTH = 500;
const PLACEHOLDER_TEXT = 'Click to add notes';

interface INoteCellRendererProps extends ICellRendererParams {
    value: string | null | undefined;
    truncateLength?: number;
}

const truncateText = (text: string, maxLength: number): string => {
    if (text.length <= maxLength) {
        return text;
    }

    return `${text.slice(0, maxLength).trimEnd()}...`;
};

// Editable Notes column. The Template Admin can click the cell to open a
// multi-line text editor. Only new lines/carriage returns are preserved;
// no rich text formatting is supported.
const NoteCellRenderer: React.FC<INoteCellRendererProps> = (params) => {
    const truncateLength = params.truncateLength ?? DEFAULT_NOTE_TRUNCATE_LENGTH;
    const [open, setOpen] = useState(false);
    const [draftValue, setDraftValue] = useState(params.value ?? '');

    const currentValue: string = params.value ?? '';
    const hasNote = currentValue.trim().length > 0;

    const displayText = hasNote
        ? truncateText(currentValue, truncateLength)
        : PLACEHOLDER_TEXT;

    const tooltipText = truncateText(currentValue, TOOLTIP_MAX_LENGTH);

    const handleOpen = () => {
        setDraftValue(params.value ?? '');
        setOpen(true);
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
            event.preventDefault();
            handleOpen();
        }
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleSave = () => {
        const field = params.colDef?.field;

        if (field && draftValue !== params.value) {
            params.node.setDataValue(field, draftValue);
        }

        setOpen(false);
    };

    const cellContent = (
        <Box
            role="button"
            tabIndex={0}
            aria-label={hasNote ? 'Edit notes' : 'Click to add notes'}
            onClick={handleOpen}
            onKeyDown={handleKeyDown}
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                width: '100%',
                height: '100%',
                cursor: 'pointer',
                overflow: 'hidden',
                '&:focus-visible': {
                    outline: '2px solid',
                    outlineColor: 'primary.main',
                    outlineOffset: '-2px',
                },
            }}
        >
            <Typography
                noWrap
                variant="body2"
                sx={{
                    color: hasNote ? 'inherit' : 'grey.500',
                    fontStyle: hasNote ? 'normal' : 'italic',
                    fontWeight: hasNote ? 'normal' : 300,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                }}
            >
                {displayText}
            </Typography>
        </Box>
    );

    return (
        <>
            {hasNote ? (
                <Tooltip title={tooltipText} placement="top" arrow>
                    {cellContent}
                </Tooltip>
            ) : cellContent}
            <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
                <DialogTitle>Edit Notes</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        multiline
                        fullWidth
                        minRows={8}
                        maxRows={20}
                        variant="outlined"
                        placeholder="Enter notes..."
                        value={draftValue}
                        onChange={(event) => setDraftValue(event.target.value)}
                        sx={{ marginTop: 1 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Cancel</Button>
                    <Button onClick={handleSave} variant="contained">Save</Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default NoteCellRenderer;
